import { Fragment } from 'react'
import type { FC, MouseEvent, RefObject } from 'react'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import {
  ChevronIcon,
  Divider,
  FreeformIcon,
  IconSlot,
  InfoIcon,
  ParentMenu,
  ParentMenuItem,
  SectionHeading,
  TemplatesHeading,
  TooltipDescription,
} from 'features/charts-v2/components/ChartTypePicker/chart-type-picker.styles'
import type { ChartTypePickerSectionSpec } from 'features/charts-v2/components/ChartTypePicker/ChartTypePicker'
import type { ChartPresetGroup } from 'features/charts-v2/types/chart-types'

interface ParentMenuListProps {
  parentMenuRef: RefObject<HTMLDivElement>
  sections: readonly ChartTypePickerSectionSpec[]
  visibleGroups: ChartPresetGroup[]
  activeGroup: string | null
  currentPresetGroup: ChartPresetGroup | null
  onGroupHover: (groupKey: string, event: MouseEvent<HTMLDivElement>) => void
}

/** The left pane of the picker dropdown, rendered in the order `sections`
 *  declares: `'templates'` ("One-click templates:" + a row per preset
 *  group), `'freeform'` ("Custom chart:" + the Freeform row), or a
 *  caller-supplied section object (e.g. dashboards-v2-1's "Widgets:"
 *  block) — the parent-row list that reveals a `ChildMenu` on hover. A
 *  divider separates each section from the one before it. */
export const ParentMenuList: FC<ParentMenuListProps> = ({
  parentMenuRef,
  sections,
  visibleGroups,
  activeGroup,
  currentPresetGroup,
  onGroupHover,
}) => (
  <ParentMenu ref={parentMenuRef}>
    {sections.map((section, index) => (
      <Fragment key={typeof section === 'string' ? section : section.key}>
        {index > 0 && <Divider />}
        {section === 'templates' && (
          <>
            <TemplatesHeading>
              One-click templates:
              <Tooltip
                content={
                  <div>
                    <div>Templates preserve filters and breakdowns</div>
                    <TooltipDescription>
                      Selecting a new template will not change your filters or
                      primary breakdown.
                      <br />
                      Secondary breakdowns may be removed if a single-breakdown
                      chart is selected.
                    </TooltipDescription>
                  </div>
                }
                side="right"
              >
                <InfoIcon />
              </Tooltip>
            </TemplatesHeading>
            {visibleGroups.map((group) => (
              <ParentMenuItem
                key={group.key}
                onMouseEnter={(e) => onGroupHover(group.key, e)}
                $isActive={activeGroup === group.key}
                $isSelected={currentPresetGroup?.key === group.key}
              >
                <IconSlot>{group.icon}</IconSlot>
                <span>{group.label}</span>
                <ChevronIcon />
              </ParentMenuItem>
            ))}
          </>
        )}
        {section === 'freeform' && (
          <>
            <SectionHeading>Custom chart:</SectionHeading>
            <ParentMenuItem
              onMouseEnter={(e) => onGroupHover('freeform', e)}
              $isActive={activeGroup === 'freeform'}
              $isSelected={currentPresetGroup?.key === 'freeform'}
            >
              <FreeformIcon />
              <span>Freeform</span>
              <ChevronIcon />
            </ParentMenuItem>
          </>
        )}
        {typeof section !== 'string' && (
          <>
            <SectionHeading>{section.heading}</SectionHeading>
            <ParentMenuItem
              onMouseEnter={(e) => onGroupHover(section.key, e)}
              $isActive={activeGroup === section.key}
              $isSelected={section.isSelected ?? false}
            >
              <IconSlot>{section.icon}</IconSlot>
              <span>{section.label}</span>
              <ChevronIcon />
            </ParentMenuItem>
          </>
        )}
      </Fragment>
    ))}
  </ParentMenu>
)
